'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { AuthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';
import { Chip } from './Chip';
import { LOCATION_MAP, ONBOARDING_SEQUENCE, type RegionName } from './constants';
const StampMapContent = () => {
  const $ = _c(48);
  const ref = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true,
      margin: "-80px"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isInView = useInView(ref, t0);
  const isLogin = useAuthStore(_temp);
  const [onboardingStep, setOnboardingStep] = useState(-1);
  const [onboardingCount, setOnboardingCount] = useState(0);
  const [showEmptyState, setShowEmptyState] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthQueryOptions["get:/wello/v2/auth/home-donation/event/local-tour"]();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isLogin) {
    t2 = {
      ...t1,
      enabled: isLogin,
      select: _temp2
    };
    $[2] = isLogin;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: localTourData
  } = useQuery(t2);
  const donationCount = localTourData?.donationCount ?? 0;
  const localDonationList = localTourData?.localDonationInfoList ?? [];
  const donationMap = new Map(localDonationList.map(_temp3));
  const locationsToShow = donationCount >= 5 ? localDonationList.map(_temp4) as RegionName[] : Object.keys(LOCATION_MAP) as RegionName[];
  let t3;
  let t4;
  if ($[4] !== donationCount || $[5] !== isInView) {
    t3 = () => {
      if (!isInView || donationCount > 0) {
        return;
      }
      setShowEmptyState(false);
      const TOTAL_STEPS = ONBOARDING_SEQUENCE.length;
      let rafId;
      let startTime = null;
      let phase = "up";
      const animate = timestamp => {
        if (!startTime) {
          startTime = timestamp;
        }
        const elapsed = timestamp - startTime;
        if (phase === "up") {
          const currentStep = Math.floor(elapsed / 200);
          if (currentStep < TOTAL_STEPS) {
            setOnboardingStep(currentStep);
            setOnboardingCount(currentStep + 1);
            rafId = requestAnimationFrame(animate);
          } else {
            phase = "pause";
            startTime = timestamp;
            rafId = requestAnimationFrame(animate);
          }
        } else {
          if (phase === "pause") {
            if (elapsed >= 500) {
              phase = "down";
              startTime = timestamp;
              rafId = requestAnimationFrame(animate);
            } else {
              rafId = requestAnimationFrame(animate);
            }
          } else {
            if (phase === "down") {
              const currentStep_0 = Math.floor(elapsed / 200);
              const downStep = TOTAL_STEPS - 1 - currentStep_0;
              if (downStep >= 0) {
                setOnboardingStep(downStep);
                setOnboardingCount(downStep + 1);
                rafId = requestAnimationFrame(animate);
              } else {
                phase = "complete";
                setOnboardingStep(-1);
                setOnboardingCount(0);
                setShowEmptyState(true);
              }
            }
          }
        }
      };
      rafId = requestAnimationFrame(animate);
      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t4 = [isInView, donationCount];
    $[4] = donationCount;
    $[5] = isInView;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = "flex flex-col items-center bg-[#E4302C] py-60";
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 30
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isInView) {
    t7 = isInView ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 30
    };
    $[9] = isInView;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t10;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h2 className="pb-8 font-600 text-Gray-white typo-h6">지역 스탬프를 모아보세요!</h2>;
    t10 = <h3 className="pb-20 font-800 text-Gray-white typo-h4">나의 기부 스탬프 현황</h3>;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      scale: 0.9
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isInView) {
    t12 = isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0.9
    };
    $[15] = isInView;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      duration: 0.5,
      delay: 0.2,
      ease: "easeOut"
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = donationCount > 0 || onboardingCount > 0;
  const t15 = donationCount === 0 && onboardingCount === 0;
  let t16;
  if ($[18] !== t14 || $[19] !== t15) {
    t16 = cn("rounded-pill px-20 py-10 font-800 typo-body1", {
      "text-Y-100 [background:linear-gradient(0deg,_var(--Color-Red-R-550,_#DB0101)_0%,_var(--Color-Red-R-550,_#DB0101)_100%),_var(--Color-Gray-Gray-300,_#E5E7EC)]": t14,
      "text-Gray-500 bg-Gray-300": t15
    });
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = `10개 중 ${donationCount || onboardingCount}개`;
  let t18;
  if ($[21] !== donationCount || $[22] !== onboardingCount) {
    t18 = donationCount > 0 ? <CountUp start={0} end={donationCount} duration={2} /> : onboardingCount;
    $[21] = donationCount;
    $[22] = onboardingCount;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t17 || $[25] !== t18) {
    t19 = <span aria-label={t17}>{t18}{" "}/ 10</span>;
    $[24] = t17;
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t12 || $[28] !== t16 || $[29] !== t19) {
    t20 = <motion.div layout={true} initial={t11} animate={t12} transition={t13} className={t16} role="status" aria-live="polite">기부 지역 스코어:{" "}{t19}</motion.div>;
    $[27] = t12;
    $[28] = t16;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t20 || $[32] !== t7) {
    t21 = <motion.div ref={ref} initial={t6} animate={t7} transition={t8} className="flex flex-col items-center pb-32">{t9}{t10}{t20}</motion.div>;
    $[31] = t20;
    $[32] = t7;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const t22 = "relative mx-20 w-full max-w-[20.9375rem]";
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      aspectRatio: "335/559"
    };
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  const t24 = "img";
  const t25 = `대한민국 지도에 표시된 기부 스탬프. 총 10개 중 ${donationCount}개 획득`;
  let t26;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SvgStampMap />;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  const t27 = locationsToShow.map(localName => {
    const donationInfo = donationMap.get(localName);
    const onboardingIndex = ONBOARDING_SEQUENCE.indexOf(localName);
    const isOnboardingActive = donationCount === 0 && onboardingStep >= 0 && onboardingIndex >= 0 && onboardingIndex <= onboardingStep;
    return <Chip key={localName} className={LOCATION_MAP[localName].className} localName={localName} specialtyImageUrl={donationInfo?.specialtyImageUrl} isActive={!!donationInfo || isOnboardingActive} isRecent={donationInfo?.recentYN ?? false} />;
  });
  let t28;
  if ($[36] !== donationCount || $[37] !== showEmptyState) {
    t28 = donationCount === 0 && showEmptyState && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="absolute inset-0 flex flex-col items-center justify-center gap-32 bg-[rgba(228,48,44,0.5)] backdrop-blur-sm" role="alert" aria-live="polite"><svg xmlns="http://www.w3.org/2000/svg" width="35" height="86" viewBox="0 0 35 86" fill="none"><path d="M0.23 16.18C-0.08 12.26 -0.08 4.1 0.23 0.17C3.39 -0.04 6.96 -0.04 9.81 0.07C26.13 0.69 35 9.26 35 25.17C35 35.6 29.8 43.45 20.62 46.96C20.62 50.68 20.42 54.705 20.11 58.22C16.24 58.63 6.86 58.63 3.19 58.22C2.47 49.64 2.27 39.32 2.68 35.7L6.45 35.39C14.3 34.77 17.56 30.74 17.56 25.27C17.56 19.49 13.49 16.39 8.18 16.18C5.53 16.08 3.29 16.18 0.23 16.18ZM1.35 75.67C1.35 69.89 5.94 65.34 11.65 65.34C17.36 65.34 21.95 69.89 21.95 75.67C21.95 81.46 17.36 86 11.65 86C5.94 86 1.35 81.46 1.35 75.67Z" fill="white" /></svg><p className="text-center text-[24px] font-700 leading-[1.3] tracking-[-0.75px] text-Gray-white">지금 바로 첫 번째 스탬프를<br />찍어보세요!</p></motion.div>;
    $[36] = donationCount;
    $[37] = showEmptyState;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== t23 || $[40] !== t25 || $[41] !== t26 || $[42] !== t27 || $[43] !== t28) {
    t29 = <div className={t22} style={t23} role={t24} aria-label={t25}>{t26}{t27}{t28}</div>;
    $[39] = t23;
    $[40] = t25;
    $[41] = t26;
    $[42] = t27;
    $[43] = t28;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t21 || $[46] !== t29) {
    t30 = <section className={t5}>{t21}{t29}</section>;
    $[45] = t21;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  return t30;
};
const SvgStampMap = () => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path d="M211.582 13.47L55.37 96.33C49.19 99.6 47.12 107.449 50.86 113.355L80.485 160.067C84.25 166.01 82.12 173.907 75.87 177.147L40.865 195.298C34.51 198.593 32.43 206.683 36.42 212.63L82.99 282.151C85.71 286.215 85.7 291.525 82.95 295.571L7.0 407.408C4.54 411.041 4.25 415.732 6.26 419.637L20.64 447.599C23.17 452.528 28.75 455.08 34.136 453.773L187.23 416.642C188.156 416.417 189.105 416.304 190.058 416.304H233.224C235.386 416.304 237.507 415.72 239.364 414.614L287.684 385.835C290.095 384.399 291.927 382.163 292.86 379.516L327.762 280.545C329.498 275.622 324.842 270.843 319.875 272.449C315.73 273.79 311.507 270.624 311.632 266.269L314.273 174.47C314.336 172.28 313.798 170.113 312.717 168.207L227.644 18.15C224.44 12.499 217.321 10.42 211.582 13.47Z" fill="#E95956" />;
    t1 = <path d="M41.78 489.575L83.77 480.75C86.08 480.266 88.48 480.61 90.55 481.724L121.684 498.419C127.1 501.324 128.639 508.381 124.924 513.277L106.482 537.58C104.944 539.607 102.691 540.971 100.183 541.395L46.94 550.398C44.64 550.787 42.27 550.36 40.26 549.19L15.48 534.834C10.05 531.684 8.83 524.359 12.95 519.618L36.29 492.797C37.721 491.154 39.65 490.023 41.78 489.575Z" fill="#E95956" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maskType: "alpha"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <mask id="mask0_26007_13980" style={t2} maskUnits="userSpaceOnUse" x="4" y="11" width="325" height="444"><path d="M211.582 13.47L55.37 96.33C49.19 99.6 47.12 107.449 50.86 113.355L80.485 160.067C84.25 166.01 82.12 173.907 75.87 177.147L40.865 195.298C34.51 198.593 32.43 206.683 36.42 212.63L82.99 282.151C85.71 286.215 85.7 291.525 82.95 295.571L7.0 407.408C4.54 411.041 4.25 415.732 6.26 419.637L20.64 447.599C23.17 452.528 28.75 455.08 34.136 453.773L187.23 416.642C188.156 416.417 189.105 416.304 190.058 416.304H233.224C235.386 416.304 237.507 415.72 239.364 414.614L287.684 385.835C290.095 384.399 291.927 382.163 292.86 379.516L327.762 280.545C329.498 275.622 324.842 270.843 319.875 272.449C315.73 273.79 311.507 270.624 311.632 266.269L314.273 174.47C314.336 172.28 313.798 170.113 312.717 168.207L227.644 18.15C224.44 12.499 217.321 10.42 211.582 13.47Z" fill="white" stroke="#E4302C" /></mask>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <svg width="335" height="559" viewBox="0 0 335 559" fill="FFF" xmlns="http://www.w3.org/2000/svg">{t0}{t1}{t3}<g mask="url(#mask0_26007_13980)"><path d="M134.498 51L183.765 151.745M183.765 151.745L143.559 197.023M183.765 151.745L260.214 186.27M143.559 197.023L72.21 169.29M143.559 197.023L155.529 239.512M331 163.631L260.214 186.27M260.214 186.27L207 276.5M207 276.5L306.083 397.381M207 276.5L167.5 282M207 276.5L155.529 239.512M72.21 292.5L167.5 282M167.5 282L159.5 365.106M167.5 282L155.529 239.512M159.5 365.106L39 342.5M159.5 365.106L167.5 450.5" stroke="#E4302C" strokeWidth="4" strokeLinecap="round" strokeDasharray="9 9" /></g></svg>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
export const StampMapSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <StampMapContent />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(item) {
  return [item.localName, item];
}
function _temp4(item_0) {
  return item_0.localName;
}